import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errorMessage, downloadFile } from "@/lib/api";
import { bankIdLoginUrl } from "@/lib/bankid";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AUDIT_ACTIONS } from "@/lib/constants";
import { copyText, formatDateTime, todayIso } from "@/lib/format";
import { toast } from "sonner";
import { Copy, RefreshCw, Download, Trash2, Shield, Bell, Mail, User, KeyRound, CheckCircle2, AlertTriangle, History, Loader2, Fingerprint } from "lucide-react";
import { cn } from "@/lib/utils";

export default function SettingsPage() {
  const { user, setUser, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [activity, setActivity] = useState([]);
  const [inbound, setInbound] = useState([]);
  const [delPw, setDelPw] = useState("");
  const [delOpen, setDelOpen] = useState(false);
  const [bankid, setBankid] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/me/settings").then((r) => { setSettings(r.data); setForm({ full_name: r.data.full_name, reminder_days: r.data.reminder_days, auto_logout_minutes: r.data.auto_logout_minutes, notify_in_app: r.data.notify_in_app !== false, notify_email: !!r.data.notify_email }); });
  useEffect(() => {
    load();
    api.get("/me/activity", { params: { limit: 30 } }).then((r) => setActivity(r.data.items)).catch(() => {});
    api.get("/me/inbound-emails").then((r) => setInbound(r.data.items)).catch(() => {});
    api.get("/auth/bankid/status", { skipActAs: true }).then((r) => setBankid(r.data)).catch(() => setBankid({ enabled: false }));
  }, []);

  // Retur fra BankID-kobling (?bankid=koblet|feil)
  useEffect(() => {
    const res = params.get("bankid");
    if (!res) return;
    if (res === "koblet") { toast.success("BankID er koblet til kontoen din"); refreshUser(); }
    else toast.error(params.get("melding") || "BankID kunne ikke kobles");
    const next = new URLSearchParams(params); next.delete("bankid"); next.delete("melding"); setParams(next, { replace: true });
  }, [params, setParams, refreshUser]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/me/settings", { full_name: form.full_name, reminder_days: Number(form.reminder_days), auto_logout_minutes: Number(form.auto_logout_minutes), notify_in_app: form.notify_in_app, notify_email: form.notify_email });
      setUser((u) => ({ ...u, ...res.data }));
      toast.success("Innstillinger lagret");
      load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setSaving(false); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) return toast.error("Nytt passord må ha minst 8 tegn");
    if (pw.new_password !== pw.confirm) return toast.error("Passordene er ikke like");
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Passordet er endret");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(errorMessage(err)); } finally { setPwBusy(false); }
  };

  const rotate = async () => {
    try {
      const r = await api.post("/me/forwarding-address/rotate");
      toast.success("Ny videresendingsadresse opprettet");
      setSettings((s) => ({ ...s, forwarding_address: r.data.forwarding_address }));
      setUser((u) => ({ ...u, forwarding_address: r.data.forwarding_address }));
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const exportAll = async () => {
    try { await downloadFile("/me/export", `regningsportal_eksport_${todayIso()}.json`); toast.success("Eksport lastet ned"); } catch (e) { toast.error(errorMessage(e)); }
  };
  const exportCsv = async () => {
    try { await downloadFile("/invoices/export.csv", `fakturaer_${todayIso()}.csv`); toast.success("CSV lastet ned"); } catch (e) { toast.error(errorMessage(e)); }
  };

  const deleteAccount = async () => {
    try {
      await api.post("/me/delete", { password: delPw });
      toast.success("Kontoen din er slettet");
      await logout();
      navigate("/logg-inn");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  if (!settings || !form) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-64" /><Skeleton className="h-64" /></div>;

  return (
    <div className="space-y-6">
      <PageHeader title="Innstillinger" description="Profil, påminnelser, sikkerhet, mottakskanaler og dine data." />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><User className="h-4 w-4 text-primary" /> Profil og påminnelser</CardTitle><CardDescription>{user?.email}</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5"><Label htmlFor="full_name">Navn</Label><Input id="full_name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="settings-name-input" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="reminder_days">Varsle meg før forfall</Label>
                <Select value={String(form.reminder_days)} onValueChange={(v) => setForm({ ...form, reminder_days: v })}>
                  <SelectTrigger id="reminder_days" data-testid="settings-reminder-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">{[1, 2, 3, 5, 7, 10, 14, 21, 30].map((d) => <SelectItem key={d} value={String(d)}>{d} {d === 1 ? "dag" : "dager"} før</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Styrer også hva som vises som «Forfaller snart».</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="auto_logout">Automatisk utlogging</Label>
                <Select value={String(form.auto_logout_minutes)} onValueChange={(v) => setForm({ ...form, auto_logout_minutes: v })}>
                  <SelectTrigger id="auto_logout" data-testid="settings-autologout-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">{[5, 10, 15, 30, 60, 120].map((m) => <SelectItem key={m} value={String(m)}>Etter {m} min inaktivitet</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border bg-secondary px-3 py-2.5">
              <div><p className="text-sm font-medium flex items-center gap-2"><Bell className="h-4 w-4" /> Varsler i appen</p><p className="text-xs text-muted-foreground">Forfall, duplikater og kontroller vises under Varsler.</p></div>
              <Switch checked={form.notify_in_app} onCheckedChange={(v) => setForm({ ...form, notify_in_app: v })} data-testid="settings-notify-switch" />
            </div>
            <div className={`flex items-center justify-between rounded-lg border bg-secondary px-3 py-2.5 ${settings.email_available ? "" : "opacity-80"}`}>
              <div>
                <p className="text-sm font-medium flex items-center gap-2"><Mail className="h-4 w-4" /> Påminnelser på e-post</p>
                <p className="text-xs text-muted-foreground">{settings.email_available ? `Daglig oppsummering til ${user?.email} når regninger nærmer seg forfall eller er forfalt.` : "E-post er ikke satt opp av administrator ennå. Valget lagres og aktiveres automatisk når e-post er klart."}</p>
              </div>
              <Switch checked={form.notify_email} onCheckedChange={(v) => setForm({ ...form, notify_email: v })} data-testid="settings-notify-email-switch" />
            </div>
            <p className="text-xs text-muted-foreground">Navn, adresse, org.nr, kontonummer og logo endrer du under <Link to="/profil" className="underline" data-testid="settings-profile-link">Min profil</Link>.</p>
            <Button onClick={save} disabled={saving} data-testid="settings-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Lagre innstillinger</Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" /> Sikkerhet</CardTitle><CardDescription>{bankid?.enabled ? "BankID og passord for kontoen din." : "Bytt passord. BankID-innlogging aktiveres når administrator har lagt inn avtalen."}</CardDescription></CardHeader>
          <CardContent>
            {bankid?.enabled ? (
              <div className={cn("mb-5 rounded-lg border p-3 flex flex-col sm:flex-row sm:items-center gap-3", user?.bankid_linked ? "border-success/40 bg-success/5" : "bg-secondary/60")} data-testid="settings-bankid-card">
                <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0"><Fingerprint className="h-5 w-5" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{user?.bankid_linked ? "BankID er koblet til kontoen" : "BankID er ikke koblet ennå"}</p>
                  <p className="text-xs text-muted-foreground">{user?.bankid_linked ? `Du logger inn med BankID via ${bankid.provider_label}.${bankid.only_for_users ? " Passordinnlogging er slått av for brukere." : ""}` : "Koble BankID nå, så kan du logge inn uten passord neste gang."}</p>
                </div>
                {user?.bankid_linked ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-success" data-testid="settings-bankid-linked"><CheckCircle2 className="h-4 w-4" /> Koblet</span>
                ) : (
                  <Button size="sm" onClick={() => window.location.assign(bankIdLoginUrl({ link: true }))} data-testid="settings-bankid-link-button"><Fingerprint className="h-4 w-4 mr-1.5" /> Koble BankID</Button>
                )}
              </div>
            ) : null}
            {user?.has_password === false ? (
              <p className="text-sm text-muted-foreground" data-testid="settings-no-password-hint">Kontoen din ble opprettet med BankID og har ikke noe passord. Du logger alltid inn med BankID.</p>
            ) : (
            <form onSubmit={changePw} className="space-y-3">
              <div className="space-y-1.5"><Label htmlFor="cur">Nåværende passord</Label><Input id="cur" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} autoComplete="current-password" data-testid="settings-current-password" /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label htmlFor="new">Nytt passord</Label><Input id="new" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} autoComplete="new-password" data-testid="settings-new-password" /></div>
                <div className="space-y-1.5"><Label htmlFor="conf">Gjenta nytt passord</Label><Input id="conf" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} autoComplete="new-password" data-testid="settings-confirm-password" /></div>
              </div>
              <Button type="submit" variant="outline" disabled={pwBusy || !pw.current_password || !pw.new_password} data-testid="settings-change-password-button">Endre passord</Button>
            </form>
            )}
            <div className="mt-5 rounded-lg border bg-secondary p-3 text-xs text-muted-foreground space-y-1">
              <p className="flex items-center gap-2 text-foreground font-medium"><Shield className="h-4 w-4 text-primary" /> Slik beskytter vi deg</p>
              <p>Kryptert overføring og lagring · Ingen lagring av nettbankpassord · Administrator kan ikke se fakturainnhold · Alle visninger og endringer logges.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> Mottakskanaler</CardTitle><CardDescription>Slik kan regninger komme inn i portalen. Kanaler som krever kommersiell avtale er merket.</CardDescription></CardHeader>
        <CardContent className="space-y-4">
          {settings.forwarding_address ? <div className="rounded-lg border bg-secondary p-3">
            <p className="text-sm font-medium">Din personlige e-postadresse for videresending</p>
            <div className="mt-2 flex flex-col sm:flex-row gap-2">
              <code className="flex-1 rounded-md border bg-card px-3 py-2 text-sm truncate" data-testid="settings-forwarding-address">{settings.forwarding_address}</code>
              <Button variant="outline" onClick={async () => { await copyText(settings.forwarding_address); toast.success("Adresse kopiert"); }} data-testid="settings-copy-forwarding"><Copy className="h-4 w-4 mr-2" /> Kopier</Button>
              <Button variant="ghost" onClick={rotate} data-testid="settings-rotate-forwarding"><RefreshCw className="h-4 w-4 mr-2" /> Ny adresse</Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Lag en videresendingsregel i e-posten din, eller videresend enkeltfakturaer manuelt. Kun vedlegg som er fakturaer leses.</p>
          </div> : null}
          <ul className="grid gap-2 sm:grid-cols-2" data-testid="channels-list">
            {settings.channels.map((c) => (
              <li key={c.key} className="rounded-lg border p-3 flex items-start gap-3">
                <span className={cn("mt-0.5 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium shrink-0", c.status === "aktiv" ? "status-betalt" : "status-forfaller_snart")}>{c.status === "aktiv" ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}{c.status === "aktiv" ? "Aktiv" : "Krever avtale"}</span>
                <div><p className="text-sm font-medium">{c.label}</p><p className="text-xs text-muted-foreground">{c.detail}</p></div>
              </li>
            ))}
          </ul>
          {inbound.length ? (
            <div>
              <p className="text-sm font-medium mb-2">Siste mottatte e-poster</p>
              <ul className="divide-y rounded-lg border">{inbound.slice(0, 8).map((m) => <li key={m.id} className="px-3 py-2 text-sm flex items-center justify-between gap-2"><span className="truncate">{m.subject || "(uten emne)"} <span className="text-muted-foreground">fra {m.from}</span></span><span className="text-xs text-muted-foreground shrink-0">{m.status} · {formatDateTime(m.created_at)}</span></li>)}</ul>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Download className="h-4 w-4 text-primary" /> Dine data</CardTitle><CardDescription>Du eier dataene dine. Last ned alt når du vil.</CardDescription></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={exportCsv} data-testid="settings-export-csv"><Download className="h-4 w-4 mr-2" /> Fakturaer (CSV)</Button>
            <Button variant="outline" onClick={exportAll} data-testid="settings-export-json"><Download className="h-4 w-4 mr-2" /> Alle data (JSON)</Button>
          </CardContent>
        </Card>
        <Card className="border-destructive/40">
          <CardHeader><CardTitle className="text-base flex items-center gap-2 text-destructive"><Trash2 className="h-4 w-4" /> Slett konto</CardTitle><CardDescription>Sletter alle fakturaer, dokumenter, leverandører og varsler permanent.</CardDescription></CardHeader>
          <CardContent><Button variant="destructive" onClick={() => setDelOpen(true)} data-testid="settings-delete-account-button">Slett kontoen min</Button></CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Aktivitetslogg</CardTitle><CardDescription>Siste hendelser på kontoen din.</CardDescription></CardHeader>
        <CardContent>
          {activity.length === 0 ? <p className="text-sm text-muted-foreground">Ingen aktivitet.</p> : (
            <ul className="divide-y" data-testid="activity-log">{activity.map((a) => <li key={a.id} className="py-2 flex items-center justify-between gap-3 text-sm"><span>{AUDIT_ACTIONS[a.action] || a.action}{a.ip ? <span className="text-muted-foreground"> · {a.ip}</span> : ""}</span><span className="text-xs text-muted-foreground tnum shrink-0">{formatDateTime(a.created_at)}</span></li>)}</ul>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader><AlertDialogTitle>Slette kontoen permanent?</AlertDialogTitle><AlertDialogDescription>Alle fakturaer, dokumenter og innstillinger slettes. Dette kan ikke angres. {user?.has_password === false ? "Skriv SLETT for å bekrefte." : "Skriv inn passordet ditt for å bekrefte."}</AlertDialogDescription></AlertDialogHeader>
          <Input type={user?.has_password === false ? "text" : "password"} placeholder={user?.has_password === false ? "SLETT" : "Passord"} value={delPw} onChange={(e) => setDelPw(e.target.value)} data-testid="delete-account-password" />
          <AlertDialogFooter><AlertDialogCancel>Avbryt</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" disabled={!delPw} onClick={deleteAccount} data-testid="delete-account-confirm">Slett kontoen</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
